import { f7 } from 'framework7-react';
import { useCallback, useMemo } from 'react';
import {
  EstimateDetailInfoType,
  EstimateDetailItemType,
  InsuranceResultInfoType,
  RepairItemSelectInfoType,
  RepairRequestIDInfoType,
} from '../../../types/bodyAndPaint';
import { useRepairItemSelectState } from './hook/state/useRepairItemSelectState';
import { useRepairItemSelectData } from './hook/useRepairItemSelectData';
import { useStorageItem } from './hook/useStorageItem';
import { RepairItemSelectField } from './repairItemSelect/repairItemSelectField';
import { PageTemplateComponent } from './util/common/pageTemplate/pageTemplate';
import { paths } from '@/config/paths';
import { BODY_PAINT_STATE } from '@/consts/bodyAndPaint';

export const RepairItemSelectPage = () => {
  const { stored: repairSelectItem } = useStorageItem<{
    ids: RepairRequestIDInfoType;
    insuranceResult?: InsuranceResultInfoType;
    estimateDetailList: EstimateDetailInfoType;
    repairRequestItem: {
      status: number;
      carName: string;
      factoryName: string;
    };
  }>({ key: 'repairItemSelect' });
  const { setStorageItem: setCheckDamagedImage } = useStorageItem<{
    fromPage: string;
    inspectionID: number;
  }>({ key: 'repairCheckDamagedImage' });
  const { setStorageItem: setRequestConfirmItem } = useStorageItem<{
    ids: RepairRequestIDInfoType;
    repairSelectList: RepairItemSelectInfoType;
    repairRequestItem: {
      status: number;
      carName: string;
      category: string;
      factoryName: string;
      totalAmount: number;
    };
  }>({ key: 'repairRequestConfirmItem' });

  const {
    ids,
    inspectionID,
    insuranceResult,
    estimateDetailList,
    repairRequestItem,
  } = useMemo(() => {
    return {
      ids: repairSelectItem?.ids,
      inspectionID: repairSelectItem?.ids?.inspectionID,
      insuranceResult: repairSelectItem?.insuranceResult,
      estimateDetailList: repairSelectItem?.estimateDetailList,
      repairRequestItem: repairSelectItem?.repairRequestItem,
    };
  }, [repairSelectItem]);

  const { repairItemSelectData } = useRepairItemSelectData({
    ids: ids ?? ({} as RepairRequestIDInfoType),
    insuranceResult: insuranceResult,
    estimateDetailList: estimateDetailList ?? ([] as EstimateDetailItemType[]),
  });

  const { insurance, insuranceUse } = useMemo(() => {
    return {
      insurance:
        repairItemSelectData?.insuranceResultInfo ??
        ({} as InsuranceResultInfoType),
      insuranceUse:
        repairRequestItem?.status === BODY_PAINT_STATE.APPLIES_RESULT,
    };
  }, [repairItemSelectData, repairRequestItem?.status]);

  const { selectData, repairItemList, changeSelectItem, totalAmount } =
    useRepairItemSelectState({
      insuranceUse: insuranceUse,
      initial: repairItemSelectData?.repairItemList ?? [],
    });

  const handleClickBack = () => {
    f7.views.main.router.navigate(paths.bpsRepairEstimateResult);
  };

  const handleClickCheckImage = useCallback(() => {
    setCheckDamagedImage({
      fromPage: paths.bpsRepairItemSelect,
      inspectionID: inspectionID,
    });
    f7.views.main.router.navigate(paths.bpsRepairDamagedImage);
  }, [inspectionID, setCheckDamagedImage]);

  const handleClickRepairRequest = useCallback(() => {
    setRequestConfirmItem({
      ids: ids ?? ({} as RepairRequestIDInfoType),
      repairSelectList: selectData,
      repairRequestItem: {
        status: repairRequestItem?.status ?? -1,
        carName: repairRequestItem?.carName ?? '',
        category: '傷・凹み修理',
        factoryName: repairRequestItem?.factoryName ?? '',
        totalAmount: totalAmount,
      },
    });
    f7.views.main.router.navigate(paths.bpsRepairItemRequest);
  }, [setRequestConfirmItem, ids, selectData, repairRequestItem, totalAmount]);

  return (
    <PageTemplateComponent
      pageName={'repairItemSelect'}
      title={'整備項目選択'}
      onBack={handleClickBack}
    >
      <RepairItemSelectField
        onCheckImage={handleClickCheckImage}
        isInsurance={insuranceUse}
        insurance={insurance}
        itemList={repairItemList}
        onChangeItem={changeSelectItem}
        onRequest={handleClickRepairRequest}
        totalAmount={totalAmount.toLocaleString('ja-JP')}
      />
    </PageTemplateComponent>
  );
};
